import { db, auth } from "../firebaseClient"
import { createUserWithEmailAndPassword, sendEmailVerification } from "firebase/auth"
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  collection,
  getDocs,
  collectionGroup,
  query,
  where,
} from "firebase/firestore"
import { sendOrgWelcomeEmail, sendTeamInviteEmail } from "./emailHelpers"
import { claimNesolearnId } from "./nesolearnIdHelpers"
import { logPlatformEvent } from "./eventHelpers"

// Suggests a short org code from the org name, e.g. "Africa Web3 Institute" -> "AWI"
export const suggestOrgCode = (orgName) => {
  if (!orgName?.trim()) return ""

  const words = orgName
    ?.trim()
    ?.split(/\s+/)
    ?.filter((word) => word?.length > 0)

  if (words?.length === 1) {
    // Single word org name -> first 3-4 letters, e.g. "Nesolearn" -> "NESO"
    return words?.[0]?.slice(0, 4)?.toUpperCase()
  }

  // Multiple words -> initials, e.g. "Africa Web3 Institute" -> "AWI"
  const initials = words
    ?.map((word) => word?.[0])
    ?.join("")
    ?.toUpperCase()

  return initials?.slice(0, 6) // cap length so it stays short
}

// Checks if an org code like "AWI" is already taken
export const checkOrgCodeAvailable = async (orgCode) => {
  const cleanCode = orgCode?.trim()?.toUpperCase()
  if (!cleanCode) return { available: false, error: "Org code is required" }

  try {
    const orgRef = doc(db, "organizations", cleanCode)
    const orgSnap = await getDoc(orgRef)
    return { available: !orgSnap.exists(), error: null }
  } catch (error) {
    return { available: false, error }
  }
}

// Creates the owner's auth account, the organization doc, and the owner's
// member doc. The org's Firestore doc key is now an auto-generated ID —
// registration no longer depends on picking a unique short code up front.
// The organization's .nesol ID is claimed as a separate, skippable step
// right after (see claimOrganizationNesolearnId below), mirroring the
// individual-user flow in AuthSwitch.
export const registerOrganization = async ({
  ownerName,
  email,
  password,
  orgName,
  orgType,
}) => {
  try {
    // Create the Firebase Auth account for the org owner
    const authResult = await createUserWithEmailAndPassword(auth, email, password)
    const user = authResult.user

    // Auto-generated Firestore doc ID — internal key only, never shown to users
    const orgRef = doc(collection(db, "organizations"))
    const orgId = orgRef.id

        await setDoc(orgRef, {
      orgId,
      name: orgName,
      orgType,
      ownerUid: user.uid,
      nesolearnId: null,
      verified: false,
      verificationRequested: false,
      status: "active", // "active" | "suspended"
      suspendedAt: null,
      suspensionReason: null,
      logoUrl: "",
      coverImageUrl: "",
      description: "",
      website: "",
      country: "",
      location: "",
      contactEmail: email,
      socialLinks: {},
      profileComplete: false,
      createdAt: serverTimestamp(),
      stats: {
        totalCourses: 0,
        totalLearners: 0,
        certificatesIssued: 0,
        avgCompletionRate: 0,
      },
    })

    // Add the owner as the first team member
    const memberRef = doc(db, "organizations", orgId, "members", user.uid)
    await setDoc(memberRef, {
      uid: user.uid,
      email,
      name: ownerName,
      role: "owner",
      status: "active",
      invitedAt: serverTimestamp(),
    })

    // Write the top-level user doc so the app knows this uid belongs to an org
    const userRef = doc(db, "users", user.uid)
    await setDoc(userRef, {
      uid: user.uid,
      email,
      name: ownerName,
      accountType: "organization",
      orgId,
      orgRole: "owner",
      createdAt: serverTimestamp(),
    })

    // Firebase's built-in verification link — required before dashboard access
    await sendEmailVerification(user).catch(() => {})

    // Our custom welcome email (best-effort, won't block signup if it fails).
    // No Nesolearn ID to include yet — it's claimed in the next step.
        sendOrgWelcomeEmail({
      toEmail: email,
      ownerName,
      orgName,
      orgId,
    }).catch(() => {})

    logPlatformEvent({
      type: "ORGANIZATION_CREATED",
      actorUid: user.uid,
      actorType: "user",
      orgId,
      targetPath: `organizations/${orgId}`,
      metadata: { orgName, orgType, ownerEmail: email },
    })

    return { user, orgId, error: null }
  } catch (error) {
    return { error }
  }
}

// Claims a .nesol ID for an organization, reusing the exact same shared
// namespace individuals claim from — then stamps it onto the org's own
// profile doc so it displays and resolves consistently everywhere.
export const claimOrganizationNesolearnId = async (orgId, uid, email, username) => {
  const { nesolearnId, error } = await claimNesolearnId(uid, email, username, {
    accountType: "organization",
    orgId,
  })

  if (error) return { nesolearnId: null, error }

  try {
    await updateDoc(doc(db, "organizations", orgId), { nesolearnId })
    return { nesolearnId, error: null }
  } catch (err) {
    return { nesolearnId: null, error: err }
  }
}

// Flags an org profile as having requested manual verification review.
export const requestOrganizationVerification = async (orgId) => {
  try {
    await updateDoc(doc(db, "organizations", orgId), { verificationRequested: true })
    logPlatformEvent({
      type: "ORG_VERIFICATION_REQUESTED",
      orgId,
      targetPath: `organizations/${orgId}`,
      metadata: {},
    })
    return { error: null }
  } catch (error) {
    return { error }
  }
}

// Platform-admin-only action (enforced by Firestore rules: only isPlatformAdmin()
// can update an org doc besides its owner). Suspending does NOT delete or hide
// any data — it's meant to be paired with UI/logic elsewhere that checks
// org.status === "suspended" to block that org's members from writing
// (course edits, live classes, etc.). That enforcement is a separate task from
// this helper, which only handles the flag + audit trail.
export const suspendOrganization = async (orgId, { reason, actorUid } = {}) => {
  try {
    await updateDoc(doc(db, "organizations", orgId), {
      status: "suspended",
      suspendedAt: serverTimestamp(),
      suspensionReason: reason || "",
    })
    logPlatformEvent({
      type: "ORGANIZATION_SUSPENDED",
      actorUid: actorUid || null,
      actorType: "platform_admin",
      orgId,
      targetPath: `organizations/${orgId}`,
      severity: "critical",
      metadata: { reason: reason || "" },
    })
    return { error: null }
  } catch (error) {
    return { error }
  }
}

export const reinstateOrganization = async (orgId, { actorUid } = {}) => {
  try {
    await updateDoc(doc(db, "organizations", orgId), {
      status: "active",
      suspendedAt: null,
      suspensionReason: null,
    })
    logPlatformEvent({
      type: "ORGANIZATION_REINSTATED",
      actorUid: actorUid || null,
      actorType: "platform_admin",
      orgId,
      targetPath: `organizations/${orgId}`,
      severity: "warning",
      metadata: {},
    })
    return { error: null }
  } catch (error) {
    return { error }
  }
}

// Looks up which organization (if any) a given uid belongs to
export const getUserOrgId = async (uid) => {
  try {
    const userRef = doc(db, "users", uid)
    const userSnap = await getDoc(userRef)

    if (!userSnap.exists()) return { orgId: null, error: null }

    const userData = userSnap.data()
    return { orgId: userData?.orgId || null, error: null }
  } catch (error) {
    return { orgId: null, error }
  }
}
// Reads the org's saved dashboard date-range preference (defaults to "30d")
export const getDashboardPreference = async (orgId) => {
  try {
    const orgRef = doc(db, "organizations", orgId)
    const snap = await getDoc(orgRef)
    if (!snap.exists()) return { dateRange: "30d", error: null }
    return { dateRange: snap.data()?.preferences?.dashboardDateRange || "30d", error: null }
  } catch (error) {
    return { dateRange: "30d", error }
  }
}

// Persists the org's chosen dashboard date range so it's remembered next visit
export const updateDashboardDateRange = async (orgId, rangeKey) => {
  try {
    const orgRef = doc(db, "organizations", orgId)
    await updateDoc(orgRef, { "preferences.dashboardDateRange": rangeKey })
    return { error: null }
  } catch (error) {
    return { error }
  }
}
// Lists every organization on the platform — Control Plane only. Ordinary
// org-scoped pages never need this; it's a full collection read.
export const listAllOrganizations = async () => {
  try {
    const orgsRef = collection(db, "organizations")
    const snap = await getDocs(orgsRef)
    const organizations = snap.docs?.map((d) => ({ orgId: d.id, ...d.data() }))
    return { organizations, error: null }
  } catch (error) {
    return { organizations: [], error }
  }
}

// Fetches a single organization's profile doc
export const getOrganization = async (orgId) => {
  try {
    const orgRef = doc(db, "organizations", orgId)
    const orgSnap = await getDoc(orgRef)

    if (!orgSnap.exists()) return { organization: null, error: null }

    return { organization: orgSnap.data(), error: null }
  } catch (error) {
    return { organization: null, error }
  }
}

// Updates an organization's profile fields (used by the onboarding form)
export const updateOrganizationProfile = async (orgId, profileData) => {
  try {
    const orgRef = doc(db, "organizations", orgId)
    await updateDoc(orgRef, {
      ...profileData,
      profileComplete: true,
    })
    return { error: null }
  } catch (error) {
    return { error }
  }
}

// Lists all team members (invited + active) for an organization
export const listMembers = async (orgId) => {
  try {
    const membersRef = collection(db, "organizations", orgId, "members")
    const snap = await getDocs(membersRef)
    const members = snap.docs?.map((d) => ({ memberId: d.id, ...d.data() }))
    return { members, error: null }
  } catch (error) {
    return { members: [], error }
  }
}

// Invites a new team member by email. Creates a pending record and sends an invite email.
// If they already have a Nesolearn account, they'll be linked next time they log in.
// If not, they'll be linked automatically the moment they sign up with this email.
export const inviteMember = async ({ orgId, orgName, email, role, invitedByName }) => {
  const cleanEmail = email?.trim()?.toLowerCase()
  if (!cleanEmail) return { error: { message: "Email is required" } }

  try {
    const memberRef = doc(collection(db, "organizations", orgId, "members"))
    await setDoc(memberRef, {
      email: cleanEmail,
      name: "",
      role,
      status: "invited",
      uid: null,
      invitedAt: serverTimestamp(),
    })

        sendTeamInviteEmail({
      toEmail: cleanEmail,
      orgName,
      invitedByName,
      role,
    }).catch(() => {})

    logPlatformEvent({
      type: "MEMBER_INVITED",
      orgId,
      targetPath: `organizations/${orgId}/members/${memberRef.id}`,
      metadata: { email: cleanEmail, role },
    })

    return { error: null }
  } catch (error) {
    return { error }
  }
}

// Updates a team member's role
export const updateMemberRole = async (orgId, memberId, newRole) => {
  try {
    const memberRef = doc(db, "organizations", orgId, "members", memberId)
    await updateDoc(memberRef, { role: newRole })
    logPlatformEvent({
      type: "MEMBER_ROLE_CHANGED",
      orgId,
      targetPath: `organizations/${orgId}/members/${memberId}`,
      severity: "warning",
      metadata: { newRole },
    })
    return { error: null }
  } catch (error) {
    return { error }
  }
}

// Removes a team member
export const removeMember = async (orgId, memberId) => {
  try {
    const memberRef = doc(db, "organizations", orgId, "members", memberId)
    await deleteDoc(memberRef)
    logPlatformEvent({
      type: "MEMBER_REMOVED",
      orgId,
      targetPath: `organizations/${orgId}/members/${memberId}`,
      severity: "warning",
      metadata: {},
    })
    return { error: null }
  } catch (error) {
    return { error }
  }
}

// Called after every login/signup. Checks if this email has any pending team invites
// across any organization, and if so, links this uid to that org automatically.
export const linkPendingInvitesForUser = async (uid, email) => {
  const cleanEmail = email?.trim()?.toLowerCase()
  if (!cleanEmail) return { linkedOrgId: null, error: null }

  try {
    const membersQuery = query(
      collectionGroup(db, "members"),
      where("email", "==", cleanEmail),
      where("status", "==", "invited")
    )
    const snap = await getDocs(membersQuery)

    if (snap.empty) return { linkedOrgId: null, error: null }

    // Link the first pending invite found (a user can belong to one org for now)
    const memberDoc = snap.docs?.[0]
    const orgId = memberDoc.ref.parent.parent.id
    const memberData = memberDoc.data()

    await updateDoc(memberDoc.ref, { uid, status: "active" })

    const userRef = doc(db, "users", uid)
    await setDoc(
      userRef,
      {
        uid,
        email: cleanEmail,
        accountType: "organization",
        orgId,
        orgRole: memberData?.role || "instructor",
        createdAt: serverTimestamp(),
      },
      { merge: true }
    )

    return { linkedOrgId: orgId, error: null }
  } catch (error) {
    return { linkedOrgId: null, error }
  }
}